/**
 * The state pack registry. Light on purpose: it imports only the packs' facts and phone lists, never the content modules
 * (requirements, library, setup …), so any module can ask "which state, what words" without an import cycle. The local
 * layer (county cells, state-layer checks) is resolved in localrules.ts.
 */
import type { DeptContact } from "../deptcontact.ts";
import { isSupportedState, SUPPORTED_STATES, STATE_INDEX, type StatePack, type UsState } from "./types.ts";
import { UT } from "./ut.ts";
import { UT_DEPT_CONTACTS } from "./ut-contacts.ts";
import { perStateFrom } from "./access.ts";

export * from "./types.ts";
export * from "./registry.ts";
export { perStateTable, perStateFrom, stateModule, TABLE_KEYS } from "./access.ts";
export * from "./loaders.ts";
export { UT } from "./ut.ts";

/** Every state's pack. Utah's is in this file's chunk; another state's is read from the registry once that state is loaded (loaders.ts). */
export const PACKS: Record<UsState, StatePack> = perStateFrom<StatePack, Record<string, StatePack>>("pack", (m, st) => m[st]!, UT);
const CONTACTS: Record<UsState, readonly DeptContact[]> = perStateFrom<readonly DeptContact[], Record<string, readonly DeptContact[]>>("contacts", (m, st) => m[`${st}_DEPT_CONTACTS`]!, UT_DEPT_CONTACTS);

/** The pack for a state. Anything that isn't a supported state code gets Utah's: the app's default (and every place made before packs). */
export const packFor = (state?: string | null): StatePack => PACKS[isSupportedState(state) ? state : "UT"];
/** A stored or sent state code, or Utah when it's missing or not one we support (older rows have no state). */
export const stateOrDefault = (state?: string | null): UsState => (isSupportedState(state) ? state : "UT");
/** The state's own IANA time zone (its pack): a business's billing days and invite days are counted in it, never Utah's for all. */
export const timeZoneOf = (state?: string | null): string => packFor(state).timezone;
/** A state's name. Reads the light index, so it works before that state's pack has loaded (the state picker). */
export const stateName = (state?: string | null): string => STATE_INDEX[stateOrDefault(state)].name;
/** Each department's sourced phone numbers. */
export const contactsOf = (state: UsState): readonly DeptContact[] => CONTACTS[state];
/** The state whose public-page slug this is ("nevada" → NV), if any. */
export const stateBySlug = (slug: string): UsState | undefined => SUPPORTED_STATES.find((s) => STATE_INDEX[s].slug === slug.toLowerCase());

const normCounty = (c: string) => c.trim().toLowerCase().replace(/\s+/g, " ").replace(/\s+(county|co\.?)$/, "").trim();
/** The county as the pack spells it ("salt lake county" → "Salt Lake"), or undefined when it isn't one of the state's. */
export function countyIn(state: UsState, county: string | null | undefined): string | undefined {
  if (!county) return undefined;
  const n = normCounty(county);
  return PACKS[state].counties.find((c) => normCounty(c) === n);
}
/** Is this a county of that state? An empty county ("Not sure yet") is allowed: only state rules show until it's set. */
export const validCounty = (state: UsState, county: string | null | undefined): boolean => !county?.trim() || countyIn(state, county) !== undefined;

/** The state whose rules apply at a place: its own state (migration 0067), else its organization's, else Utah (older rows). */
export const placeState = (location?: { state?: string | null } | null, org?: { state?: string | null } | null): UsState =>
  stateOrDefault(location?.state ?? org?.state);
export * from "./icons.ts";

/**
 * A county as a person says it: "Salt Lake County", but "Long Beach" for a city program ("Long Beach (city)" in the picker),
 * "Kings County (Brooklyn)" for a New York City borough and "Carson City" for a consolidated city, never "Long Beach (city) County".
 */
export function countyLabel(county: string): string {
  const c = county.trim();
  if (/ \(city\)$/.test(c)) return c.replace(/ \(city\)$/, "");
  const borough = /^(.+) \((.+)\)$/.exec(c);
  if (borough) return `${borough[1]} County (${borough[2]})`;
  return / City$/.test(c) ? c : `${c} County`;
}
